
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, RefreshCw, Building2, AlertCircle, ChevronRight, ShieldAlert, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ComplianceViolationsTab } from './ComplianceViolationsTab';
import { CompliancePermitsTab } from './CompliancePermitsTab';

const STATUS_CATEGORIES = {
  'Permitted / Issued': [
    'Permit Entire', 'Permit Issued', 'Permitted',
    'PERMIT ISSUED - ENTIRE JOB/WORK', 'PERMIT ISSUED - PARTIAL JOB',
  ],
  'Approved': [
    'Approved', 'PLAN EXAM - APPROVED', 'PAA Approved', 'LOC Issued',
    'CO Issued', 'TA Certificate of Operation Issued', 'PA Certificate of Operation Issued',
  ],
  'In Review': [
    'Plan Examiner Review', 'PLAN EXAM - IN PROCESS', 'Chief Plan Examiner Review',
    'Pending Plan Examiner Assignment', 'Pre-Filing', 'PRE-FILING',
    'APPLICATION ASSIGNED TO PLAN EXAMINER', 'Pending Payment',
    'Corrections Under Review', 'Permitted – Corrections Under Review',
  ],
  'Objections / Issues': [
    'Objections', 'PLAN EXAM - DISAPPROVED', 'Rejected', 'QA Failed', 'Incomplete',
    'On Hold – Administrative Action', 'OnHold-NoGoodCheck', 'On Hold - Returned Check',
    'Intent to Revoke', 'Intent to Revoke - Under Review', 'OnHold - Administrative Action',
    'SUSPENDED', 'Pre-Filing - Penalty Review',
  ],
  'Completed / Signed Off': [
    'Complete', 'SIGNED OFF', 'Signed off', 'COMPLETED',
    'Full Demolition Signed-off', 'Inspections Complete',
    'APPLICATION PROCESSED - ENTIRE', 'APPLICATION PROCESSED - NO PLAN EXAM',
  ],
  'Withdrawn / Expired': [
    'Expired', 'Cancel', 'Filing Withdrawn', 'Withdrawn', 'Revoked',
    'On Hold - Applicant of Record Withdrawn',
  ],
};

const CATEGORY_COLORS = {
  'Permitted / Issued': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Approved': 'bg-green-500/15 text-green-400 border-green-500/30',
  'In Review': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'Objections / Issues': 'bg-red-500/15 text-red-400 border-red-500/30',
  'Completed / Signed Off': 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  'Withdrawn / Expired': 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};

const CATEGORY_DOT_COLORS = {
  'Permitted / Issued': 'bg-emerald-400',
  'Approved': 'bg-green-400',
  'In Review': 'bg-amber-400',
  'Objections / Issues': 'bg-red-400',
  'Completed / Signed Off': 'bg-teal-400',
  'Withdrawn / Expired': 'bg-zinc-400',
};

function getCategory(status) {
  for (const [cat, statuses] of Object.entries(STATUS_CATEGORIES)) {
    if (statuses.includes(status)) return cat;
  }
  return 'In Review';
}

export function DobDashboardTab({ onOpenProject }) {
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subTab, setSubTab] = useState('filings');
  const [mismatches, setMismatches] = useState([]);
  const [showMismatchList, setShowMismatchList] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const r = await api.get('/dob/dashboard');
      setFilings(r.data.filings || []);
    } catch { setFilings([]); }
    setLoading(false);
  }, []);

  const fetchMismatches = useCallback(async () => {
    try {
      const r = await api.get('/compliance/bin-mismatches');
      setMismatches(r.data.projects || []);
    } catch { setMismatches([]); }
  }, []);

  useEffect(() => { fetchAll(); fetchMismatches(); }, [fetchAll, fetchMismatches]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await api.post('/dob/refresh-all');
      await fetchAll();
      toast.success('All DOB statuses refreshed');
    } catch { toast.error('Failed to refresh'); }
    setRefreshing(false);
  };

  const filtered = filings.filter(f => {
    const cat = getCategory(f.filing_status);
    if (categoryFilter !== 'all' && cat !== categoryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (f.job_number || '').toLowerCase().includes(q)
        || (f.project_number || '').toLowerCase().includes(q)
        || (f.project_name || '').toLowerCase().includes(q)
        || (f.section_name || '').toLowerCase().includes(q)
        || (f.filing_status || '').toLowerCase().includes(q)
        || (f.address || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Summary counts by category
  const categoryCounts = {};
  for (const f of filings) {
    const cat = getCategory(f.filing_status);
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  }

  const filingsBody = loading ? (
    <p className="text-sm text-muted-foreground text-center py-8">Loading DOB statuses...</p>
  ) : filings.length === 0 ? (
    <Card className="border-border bg-card shadow-none">
      <CardContent className="py-12 text-center">
        <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No DOB job numbers linked to any sections yet.</p>
        <p className="text-xs text-muted-foreground mt-1">Edit a contract section in a project and add a DOB Job Number to start tracking.</p>
      </CardContent>
    </Card>
  ) : (
    <div className="space-y-3">
      {/* Summary cards */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {Object.keys(STATUS_CATEGORIES).map(cat => (
          <Card
            key={cat}
            className={`border-border shadow-none cursor-pointer transition-colors hover:border-primary/30 ${categoryFilter === cat ? 'border-primary/50 bg-primary/5' : 'bg-card'}`}
            onClick={() => setCategoryFilter(categoryFilter === cat ? 'all' : cat)}
            data-testid={`dob-cat-${cat.replace(/[^a-z]/gi, '-').toLowerCase()}`}
          >
            <CardContent className="p-2.5 text-center">
              <div className={`h-2 w-2 rounded-full mx-auto mb-1 ${CATEGORY_DOT_COLORS[cat] || 'bg-muted-foreground'}`} />
              <p className="text-lg font-bold">{categoryCounts[cat] || 0}</p>
              <p className="text-xs text-muted-foreground leading-tight">{cat}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search job #, project, section, status..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border w-72" data-testid="dob-dash-search" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 bg-card border-border text-xs h-9" aria-label="Filter by category" data-testid="dob-dash-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Categories</SelectItem>
            {Object.keys(STATUS_CATEGORIES).map(c => <SelectItem key={c} value={c} className="text-xs">{c} ({categoryCounts[c] || 0})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={refreshAll} disabled={refreshing} className="text-xs h-9 border-border" data-testid="dob-dash-refresh-all">
          <RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Refreshing...' : 'Refresh All'}
        </Button>
        {(search || categoryFilter !== 'all') && (
          <button className="text-xs text-primary hover:underline ml-1" onClick={() => { setSearch(''); setCategoryFilter('all'); }}>Clear filters</button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} of {filings.length} filings</span>
      </div>

      {/* Table */}
      <Card className="border-border bg-card shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider">Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Section</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Job #</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Dataset</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Permit Issued</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Permit Expires</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground text-xs py-8">No filings match your filters.</TableCell></TableRow>
              ) : filtered.map((f, i) => {
                const cat = getCategory(f.filing_status);
                return (
                  <TableRow key={i} className="border-border hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => onOpenProject?.(f.project_id)} data-testid={`dob-row-${i}`}>
                    <TableCell className="text-xs">
                      <span className="font-mono text-primary">{f.project_number}</span>
                      <span className="text-muted-foreground ml-1.5 hidden lg:inline">{f.project_name}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{f.section_number ? `${f.section_number} – ` : ''}{f.section_name}</TableCell>
                    <TableCell className="text-xs font-mono">{f.job_filing_number || f.job_number}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{f.dataset_short}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs border ${CATEGORY_COLORS[cat]}`}>{f.filing_status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{f.permit_issued_date || '-'}</TableCell>
                    <TableCell className={`text-xs ${f.permit_expiration_date ? 'text-amber-400' : 'text-muted-foreground'}`}>{f.permit_expiration_date || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{f.address || '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  // Project IDs flagged as BIN↔address mismatches (reuses the SAME stored data
  // the banner uses — /compliance/bin-mismatches reads bin_address_validation
  // as-is, no recomputation). Used to show an inline chip on matching rows.
  const mismatchProjectIds = new Set(mismatches.map(m => m.id));

  return (
    <div className="space-y-3">
      {/* BIN↔address mismatch banner (reuses stored bin_address_validation only) */}
      {mismatches.length > 0 && (
        <div data-testid="bin-mismatch-banner-wrap">
          <button
            type="button"
            onClick={() => setShowMismatchList(v => !v)}
            className="w-full flex items-center gap-2 text-left text-sm text-amber-400/90 border border-amber-500/30 bg-amber-500/10 rounded-md px-3 py-2 hover:bg-amber-500/15 transition-colors"
            data-testid="bin-mismatch-banner"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="flex-1">{mismatches.length} project{mismatches.length === 1 ? '' : 's'} {mismatches.length === 1 ? 'has' : 'have'} a BIN that may not match their address on file</span>
            <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${showMismatchList ? 'rotate-90' : ''}`} />
          </button>
          {showMismatchList && (
            <div className="mt-1.5 rounded-md border border-amber-500/20 bg-card divide-y divide-border overflow-hidden" data-testid="bin-mismatch-list">
              {mismatches.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onOpenProject?.(m.id)}
                  className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-secondary/40 transition-colors"
                  data-testid={`bin-mismatch-item-${i}`}
                >
                  <span className="font-mono text-xs text-primary shrink-0">{m.project_number || '—'}</span>
                  <span className="text-xs text-foreground truncate">{m.name}</span>
                  <span className="text-xs text-muted-foreground truncate hidden sm:inline">· {m.address || 'No address on file'}</span>
                  {m.bin_number && <span className="text-xs text-muted-foreground shrink-0 ml-auto">BIN {m.bin_number}</span>}
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compliance Overview sub-tabs */}
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList>
          <TabsTrigger value="filings" className="text-xs" data-testid="compliance-subtab-filings"><Building2 className="h-3 w-3 mr-1" /> Filings</TabsTrigger>
          <TabsTrigger value="violations" className="text-xs" data-testid="compliance-subtab-violations"><ShieldAlert className="h-3 w-3 mr-1" /> Violations</TabsTrigger>
          <TabsTrigger value="permits" className="text-xs" data-testid="compliance-subtab-permits"><Wrench className="h-3 w-3 mr-1" /> Permits &amp; Devices</TabsTrigger>
        </TabsList>
        <TabsContent value="filings" className="mt-3" data-testid="compliance-filings-content">
          {filingsBody}
        </TabsContent>
        <TabsContent value="violations" className="mt-3" data-testid="compliance-violations-content">
          <ComplianceViolationsTab onOpenProject={onOpenProject} mismatchProjectIds={mismatchProjectIds} />
        </TabsContent>
        <TabsContent value="permits" className="mt-3" data-testid="compliance-permits-content">
          <CompliancePermitsTab onOpenProject={onOpenProject} mismatchProjectIds={mismatchProjectIds} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
